import type { FlowMessageDto } from '@/apis/backend/generated/models';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KLAVIYO_CONFIG } from '@/config/esp-config';
import { useQueryClient } from '@tanstack/react-query';
import { FlowGraphView } from '@/components/flows/FlowGraphView';
import { klaviyoFlowUrl } from '@/components/flows/klaviyo-links';
import { useFlowStaging } from '@/components/flows/use-flow-staging';
import { FlowPublishButton } from '@/components/flows/FlowPublishButton';
import { buildFlowTriggerLabel } from '@/components/flows/flow-trigger-label';
import { useOrganization, useSyncTenantFromParams } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { AssignFlowTemplateDialog } from '@/components/flows/AssignFlowTemplateDialog';
import { FlowVariantCompareDialog } from '@/components/flows/FlowVariantCompareDialog';
import { useTemplatesControllerListTemplates } from '@/apis/backend/generated/templates/templates';
import {
  useFlowControllerGet,
  useFlowControllerRefresh,
} from '@/apis/backend/generated/flows/flows';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';

export const Route = createFileRoute('/_authed/dashboard_/o/$orgId/c/$clientId/flows/$flowId')({
  component: FlowDetailPage,
});

interface CompareTarget {
  message: FlowMessageDto;
  members: FlowMessageDto[];
}

function pct(value?: number): string {
  if (value === undefined || value === null) return '--';
  return `${(value * 100).toFixed(1)}%`;
}

function money(value?: number): string {
  if (value === undefined || value === null) return '$0';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function relativeTime(
  iso: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return t('time.justNow');
  if (mins < 60) return t('time.minAgo', { value: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('time.hrAgo', { value: hours });
  return t('time.dayAgo', { value: Math.round(hours / 24) });
}

/** A compact KPI shown in the full-screen header strip. */
function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 96 }}>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function FlowDetailPage() {
  const { t } = useTranslation('flows');
  const navigate = useNavigate();
  const { flowId, orgId: routeOrgId, clientId: routeClientId } = Route.useParams();
  useSyncTenantFromParams(routeOrgId, routeClientId);
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';

  const [editStep, setEditStep] = useState<FlowMessageDto | null>(null);
  const [compare, setCompare] = useState<CompareTarget | null>(null);
  const queryClient = useQueryClient();
  const staging = useFlowStaging({ orgId, clientId, flowId });

  const { data: flow, isLoading, error } = useFlowControllerGet(orgId, clientId, flowId, {
    query: { enabled: !!orgId && !!clientId && !!flowId, retry: false },
  });

  // When a step has a staged template (esp. a just-created email designed in the
  // editor), reflect its current name/thumbnail in the "Pending publish" preview
  // instead of the stale snapshot captured at stage time.
  const { data: templatesData } = useTemplatesControllerListTemplates(orgId, clientId, undefined, {
    query: { enabled: !!orgId && !!clientId && staging.stagedCount > 0 },
  });
  const enrichedStaged = useMemo(() => {
    const byId = new Map((templatesData?.templates ?? []).map((tpl) => [tpl.id, tpl]));
    return Object.fromEntries(
      Object.entries(staging.staged).map(([messageId, assignment]) => {
        const tpl = byId.get(assignment.templateId);
        return [
          messageId,
          tpl ? { ...assignment, name: tpl.name, thumbnailUrl: tpl.thumbnailUrl } : assignment,
        ];
      })
    );
  }, [templatesData, staging.staged]);

  const refresh = useFlowControllerRefresh({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/flows/');
          },
        });
      },
    },
  });

  const flowConfig = KLAVIYO_CONFIG.flow;
  const canAssign = !!flowConfig?.supportsTemplateAssignment;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Button
          color="inherit"
          size="small"
          startIcon={<Iconify icon="solar:alt-arrow-left-linear" width={18} />}
          onClick={() => navigate({ to: paths.dashboard.tenant(orgId, clientId).flows })}
        >
          {t('detail.allFlows')}
        </Button>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
            {flow?.name ?? ''}
          </Typography>
          {flow && (
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
              <Chip
                label={flow.status}
                size="small"
                color={flow.status === 'live' ? 'success' : 'default'}
                variant={flow.status === 'live' ? 'filled' : 'outlined'}
                sx={{ textTransform: 'capitalize', height: 20, fontSize: 11 }}
              />
              {flow.triggerType && (
                <Chip
                  label={flow.triggerType}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'capitalize', height: 20, fontSize: 11 }}
                />
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        {staging.error && (
          <Typography variant="caption" color="error.main">
            {t('publish.failed')}
          </Typography>
        )}
        <FlowPublishButton
          stagedCount={staging.stagedCount}
          isLive={flow?.status === 'live'}
          publishing={staging.publishing}
          onPublish={staging.publish}
        />

        <Typography variant="caption" color={refresh.isError ? 'error.main' : 'text.disabled'}>
          {refresh.isPending
            ? t('detail.syncing')
            : refresh.isError
              ? t('detail.refreshFailed')
              : flow?.syncedAt
                ? t('detail.synced', { time: relativeTime(flow.syncedAt, t) })
                : ''}
        </Typography>
        <Button
          color="inherit"
          variant="outlined"
          size="small"
          disabled={refresh.isPending}
          startIcon={
            <Iconify
              icon="solar:refresh-linear"
              width={18}
              sx={
                refresh.isPending
                  ? {
                      '@keyframes flow-refresh-spin': { to: { transform: 'rotate(360deg)' } },
                      animation: 'flow-refresh-spin 1s linear infinite',
                    }
                  : undefined
              }
            />
          }
          onClick={() => refresh.mutate({ orgId, clientId, flowId })}
        >
          {t('detail.refresh')}
        </Button>
        <Button
          color="inherit"
          variant="outlined"
          size="small"
          startIcon={<Iconify icon="solar:chart-2-linear" width={18} />}
          onClick={() => navigate({ to: paths.dashboard.tenant(orgId, clientId).analytics })}
        >
          {t('detail.viewPerformance')}
        </Button>
      </Stack>

      {/* KPI strip */}
      {flow && (
        <Stack
          direction="row"
          spacing={4}
          sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
        >
          <Kpi label={t('detail.kpi.recipients')} value={(flow.recipients ?? 0).toLocaleString()} />
          <Kpi label={t('detail.kpi.openRate')} value={pct(flow.openRate)} />
          <Kpi label={t('detail.kpi.clickRate')} value={pct(flow.clickRate)} />
          <Kpi label={t('detail.kpi.revenue')} value={money(flow.revenue)} />
        </Stack>
      )}

      {/* Canvas */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {isLoading && (
          <Stack spacing={2} sx={{ p: 3 }}>
            <Skeleton height={120} />
            <Skeleton height={120} />
          </Stack>
        )}
        {!!error && (
          <Typography color="error" sx={{ p: 3 }}>
            {t('detail.notFound')}
          </Typography>
        )}
        {!isLoading && !error && flow && (
          <FlowGraphView
            messages={flow.messages}
            triggerLabel={buildFlowTriggerLabel(t, flow.triggerType, flow.triggerName)}
            klaviyoFlowUrl={klaviyoFlowUrl(flow.externalId)}
            canAssign={canAssign}
            onSwap={canAssign ? (m) => setEditStep(m) : undefined}
            onCompare={(message, members) => setCompare({ message, members })}
            staged={enrichedStaged}
            height="100%"
          />
        )}
      </Box>

      <FlowVariantCompareDialog
        open={!!compare}
        members={compare?.members ?? []}
        winnerMetric={compare?.message.winnerMetric}
        canAssign={canAssign}
        onSwap={canAssign ? (m) => setEditStep(m) : undefined}
        onClose={() => setCompare(null)}
      />

      {flow && (
        <AssignFlowTemplateDialog
          open={!!editStep}
          orgId={orgId}
          clientId={clientId}
          flowId={flowId}
          editable={flow.editable}
          message={editStep}
          stagedTemplateId={editStep ? staging.staged[editStep.id]?.templateId : null}
          onClose={() => setEditStep(null)}
          onStage={(a) => {
            if (editStep) staging.stage(editStep.id, a);
          }}
        />
      )}
    </Box>
  );
}
